import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Container, Row, Col, Card, Form, Button, Table, Badge, Dropdown, Modal } from 'react-bootstrap';
import { 
  FaSearch, 
  FaCheckCircle,
  FaTimesCircle,
  FaEye,
  FaDownload,
  FaFileInvoice,
  FaMoneyBillWave,
  FaEllipsisV,
  FaPrint
} from 'react-icons/fa';
import '../../../../assets/css/admin/dashboard/PaymentHistory.css';
import AdminLayout from '../../../../layouts/admin/AdminLayout';

const PaymentHistory = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [paymentsPerPage] = useState(10);

  // ── Detail modal states ──
  const [showModal, setShowModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  const fetchBookings = () => {
    setLoading(true);
    axios.post('http://127.0.0.1:8000/api/admin/bookings/viewAllBookings')
      .then((response) => {
        if (response.data.success) {
          setPayments(response.data.bookings);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const totalPayments = payments.length;
  const successfulPayments = payments.filter(p => p.paymentStatus === 'paid').length;
  const failedPayments = payments.filter(p => p.paymentStatus === 'failed').length;
  const totalRevenue = payments
    .filter(p => p.paymentStatus === 'paid')
    .reduce((sum, p) => sum + (Number(p.advanceAmount) || 0), 0);

  const stats = [
    { label: 'Total Payments', value: totalPayments, color: 'purple', icon: '📊' },
    { label: 'Successful', value: successfulPayments, color: 'green', icon: '✅' },
    { label: 'Failed', value: failedPayments, color: 'red', icon: '❌' },
    { label: 'Revenue', value: `${(totalRevenue / 1000000).toFixed(1)}M PKR`, color: 'blue', icon: '💰' }
  ];

  const filteredPayments = payments.filter(payment => {
    const customerName = payment.wecustomer 
      ? `${payment.wecustomer.firstName} ${payment.wecustomer.lastName}` 
      : '';
    const matchesSearch = 
      String(payment.id).toLowerCase().includes(searchTerm.toLowerCase()) ||
      customerName.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || 
      (payment.paymentStatus || 'unpaid').toLowerCase() === statusFilter.toLowerCase();

    return matchesSearch && matchesStatus;
  });

  const indexOfLastPayment = currentPage * paymentsPerPage;
  const indexOfFirstPayment = indexOfLastPayment - paymentsPerPage;
  const currentPayments = filteredPayments.slice(indexOfFirstPayment, indexOfLastPayment);
  const totalPages = Math.ceil(filteredPayments.length / paymentsPerPage);

  const formatCurrency = (amount) => {
    if (!amount) return '0';
    if (amount >= 1000) return `${(amount / 1000).toFixed(0)}k`;
    return amount.toString();
  };

  // ── NEW: ab 'paid' status ko "Advance Paid" vs "Full Paid" mein split karta hai,
  // depending on whether remainingAmount abhi bhi 0 se zyada hai ya nahi ──
  const getPaymentStatus = (payment) => {
    const status = (payment?.paymentStatus || 'unpaid').toLowerCase();
    const remaining = Number(payment?.remainingAmount) || 0;

    if (status === 'paid') {
      if (remaining > 0) {
        return { bg: 'info', icon: <FaCheckCircle />, text: 'Advance Paid' };
      }
      return { bg: 'success', icon: <FaCheckCircle />, text: 'Full Paid' };
    }
    if (status === 'failed') return { bg: 'danger', icon: <FaTimesCircle />, text: 'Failed' };
    if (status === 'pending') return { bg: 'warning', icon: <FaTimesCircle />, text: 'Pending' };
    return { bg: 'secondary', icon: <FaTimesCircle />, text: 'Unpaid' };
  };

  // 3) onClick — ab modal kholega
  const handleViewDetails = (payment) => {
    setSelectedPayment(payment);
    setShowModal(true);
  };

  const handleDownloadInvoice = (id) => console.log('Download invoice:', id);
  const handlePrint = (id) => console.log('Print payment:', id);

  return (
    <AdminLayout>
      <div className="ph-payment-history">
        <Container fluid>
          <div className="ph-page-header">
            <div className="ph-header-content">
              <h2 className="ph-page-title">Payment History</h2>
              <p className="ph-page-subtitle">All completed and failed payments</p>
            </div>
            <div className="ph-header-actions">
              <Button className="ph-action-btn ph-btn-secondary">
                <FaPrint className="ph-btn-icon" /> Print Report
              </Button>
              <Button className="ph-action-btn ph-btn-primary">
                <FaDownload className="ph-btn-icon" /> Export Data
              </Button>
            </div>
          </div>

          <Row className="ph-stats-row g-4 mb-4">
            {stats.map((stat, index) => (
              <Col key={index} xl={3} lg={6} md={6} sm={12}>
                <Card className={`ph-stat-card ph-stat-${stat.color}`}>
                  <Card.Body className="ph-stat-body">
                    <div className="ph-stat-icon-box">
                      <span className="ph-stat-icon">{stat.icon}</span>
                    </div>
                    <div className="ph-stat-content">
                      <h3 className="ph-stat-value">{stat.value}</h3>
                      <p className="ph-stat-label">{stat.label}</p>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          <Card className="ph-content-card">
            <Card.Body className="ph-card-body">
              <div className="ph-filters-section">
                <Row className="g-3">
                  <Col lg={6} md={12} sm={12}>
                    <div className="ph-filter-group">
                      <label className="ph-filter-label">
                        <FaSearch className="ph-label-icon" />
                        Search by customer / booking
                      </label>
                      <div className="ph-search-wrapper">
                        <FaSearch className="ph-input-icon" />
                        <Form.Control
                          type="text"
                          placeholder="Search by booking ID, customer name..."
                          className="ph-search-input"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                    </div>
                  </Col>

                  <Col lg={6} md={6} sm={12}>
                    <div className="ph-filter-group">
                      <label className="ph-filter-label">Filter: Status</label>
                      <div className="ph-select-wrapper">
                        <Form.Select
                          className="ph-filter-select"
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                        >
                          <option value="all">All Status</option>
                          <option value="paid">Paid</option>
                          <option value="pending">Pending</option>
                          <option value="failed">Failed</option>
                          <option value="unpaid">Unpaid</option>
                        </Form.Select>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>

              <div className="ph-table-wrapper">
                <Table responsive className="ph-table">
                  <thead className="ph-table-head">
                    <tr>
                      <th className="ph-th">Booking ID</th>
                      <th className="ph-th">Customer</th>
                      <th className="ph-th">Total</th>
                      <th className="ph-th">Advance</th>
                      <th className="ph-th">Remaining</th>
                      <th className="ph-th">Status</th>
                      <th className="ph-th ph-th-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="ph-table-body">
                    {loading ? (
                      <tr><td colSpan="7" className="ph-no-data">Loading...</td></tr>
                    ) : currentPayments.length > 0 ? (
                      currentPayments.map((payment) => (
                        <tr key={payment.id} className="ph-table-row">
                          <td className="ph-td ph-td-id">
                            <span className="ph-booking-id">#{payment.id}</span>
                          </td>
                          <td className="ph-td">
                            <div className="ph-customer-info">
                              <div className="ph-customer-avatar">
                                {payment.wecustomer?.firstName?.charAt(0) || 'U'}
                              </div>
                              <div className="ph-customer-details">
                                <span className="ph-customer-name">
                                  {payment.wecustomer 
                                    ? `${payment.wecustomer.firstName} ${payment.wecustomer.lastName}` 
                                    : 'Unknown'}
                                </span>
                                <span className="ph-customer-email">
                                  {payment.wecustomer?.email}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="ph-td ph-td-amount">
                            <div className="ph-amount-wrapper">
                              <FaMoneyBillWave className="ph-money-icon" />
                              <span className="ph-amount">{formatCurrency(payment.totalAmount)}</span>
                            </div>
                          </td>
                          <td className="ph-td ph-td-amount">
                            <span className="ph-amount">{formatCurrency(payment.advanceAmount)}</span>
                          </td>
                          <td className="ph-td ph-td-amount">
                            <span className="ph-amount">{formatCurrency(payment.remainingAmount)}</span>
                          </td>
                          <td className="ph-td">
                            <Badge bg={getPaymentStatus(payment).bg} className="ph-status-badge">
                              {getPaymentStatus(payment).icon}
                              {getPaymentStatus(payment).text}
                            </Badge>
                          </td>
                          <td className="ph-td ph-td-actions">
                            <div className="ph-action-buttons">
                              <button className="ph-icon-btn ph-btn-view" onClick={() => handleViewDetails(payment)} title="View Details">
                                <FaEye />
                              </button>
                              <button className="ph-icon-btn ph-btn-download" onClick={() => handleDownloadInvoice(payment.id)} title="Download Invoice">
                                <FaFileInvoice />
                              </button>
                              <Dropdown className="ph-more-dropdown" align="end">
                                <Dropdown.Toggle className="ph-icon-btn ph-btn-more" variant="link">
                                  <FaEllipsisV />
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="ph-dropdown-menu">
                                  <Dropdown.Item onClick={() => handleViewDetails(payment)}>
                                    <FaEye className="ph-dropdown-icon" /> View Full Details
                                  </Dropdown.Item>
                                  <Dropdown.Item onClick={() => handleDownloadInvoice(payment.id)}>
                                    <FaFileInvoice className="ph-dropdown-icon" /> Download Invoice
                                  </Dropdown.Item>
                                  <Dropdown.Item onClick={() => handlePrint(payment.id)}>
                                    <FaPrint className="ph-dropdown-icon" /> Print Receipt
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="ph-no-data">
                          <div className="ph-no-data-content">
                            <span className="ph-no-data-icon">💳</span>
                            <p className="ph-no-data-text">No payment records found</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {filteredPayments.length > paymentsPerPage && (
                <div className="ph-pagination">
                  <Button className="ph-page-btn ph-prev-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
                    Previous
                  </Button>
                  <div className="ph-page-numbers">
                    {[...Array(totalPages)].map((_, index) => (
                      <button
                        key={index}
                        className={`ph-page-number ${currentPage === index + 1 ? 'ph-active' : ''}`}
                        onClick={() => setCurrentPage(index + 1)}
                      >
                        {index + 1}
                      </button>
                    ))}
                  </div>
                  <Button className="ph-page-btn ph-next-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
                    Next
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Container>

        {/* ── Payment Detail Modal ── */}
        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>Payment Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedPayment && (
              <Row className="g-4">
                <Col md={6}>
                  <label className="ph-filter-label">Booking ID</label>
                  <p>#{selectedPayment.id}</p>
                </Col>
                <Col md={6}>
                  <label className="ph-filter-label">Status</label>
                  <div>
                    <Badge bg={getPaymentStatus(selectedPayment).bg}>
                      {getPaymentStatus(selectedPayment).icon} {getPaymentStatus(selectedPayment).text}
                    </Badge>
                  </div>
                </Col>
                <Col md={6}>
                  <label className="ph-filter-label">Customer Name</label>
                  <p>{selectedPayment.wecustomer ? `${selectedPayment.wecustomer.firstName} ${selectedPayment.wecustomer.lastName}` : 'Unknown'}</p>
                </Col>
                <Col md={6}>
                  <label className="ph-filter-label">Customer Email</label>
                  <p>{selectedPayment.wecustomer?.email || '—'}</p>
                </Col>
                <Col md={6}>
                  <label className="ph-filter-label">Event</label>
                  <p>{selectedPayment.weevent?.eventName || '—'}</p>
                </Col>
                <Col md={6}>
                  <label className="ph-filter-label">Venue</label>
                  <p>{selectedPayment.wevenue?.venueName || '—'}</p>
                </Col>
                <Col md={4}>
                  <label className="ph-filter-label">Total Amount</label>
                  <p>PKR {Number(selectedPayment.totalAmount || 0).toLocaleString()}</p>
                </Col>
                <Col md={4}>
                  <label className="ph-filter-label">Advance Paid</label>
                  <p>PKR {Number(selectedPayment.advanceAmount || 0).toLocaleString()}</p>
                </Col>
                <Col md={4}>
                  <label className="ph-filter-label">Remaining</label>
                  <p>PKR {Number(selectedPayment.remainingAmount || 0).toLocaleString()}</p>
                </Col>
                <Col md={6}>
                  <label className="ph-filter-label">Payment Method</label>
                  <p>{selectedPayment.paymentMethod || '—'}</p>
                </Col>
                <Col md={6}>
                  <label className="ph-filter-label">Stripe Payment ID</label>
                  <p style={{ fontSize: '12px', wordBreak: 'break-all' }}>{selectedPayment.stripePaymentIntentId || '—'}</p>
                </Col>
              </Row>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
          </Modal.Footer>
        </Modal>
      </div>
    </AdminLayout>
  );
};

export default PaymentHistory;